import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { trackRegister } from '../utils/meta-pixel';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  
  // Verificar se estamos na página inicial
  const isHomePage = location.pathname === '/';

  // Detecta o scroll da página
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Função para navegação de seções (só funciona na home)
  const handleSectionClick = (sectionId: string) => {
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out
        ${isScrolled 
          ? 'bg-white/5 backdrop-blur-xl border-b border-white/20' 
          : 'bg-white border-b border-gray-100'
        }
      `}
      style={isScrolled ? {
        backdropFilter: 'blur(25px) saturate(200%)',
        WebkitBackdropFilter: 'blur(25px) saturate(200%)',
        background: 'rgba(255, 255, 255, 0.08)',
      } : {}}
    >
      {/* Glassmorphism background overlay quando scrolled */}
      {isScrolled && (
        <div 
          className="absolute inset-0 bg-gradient-to-r from-white/5 via-white/3 to-white/5"
          style={{
            backdropFilter: 'blur(50px)',
            WebkitBackdropFilter: 'blur(50px)',
          }}
        />
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex justify-between items-center py-4 md:py-6">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Logo priority={true} />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {isHomePage ? (
              <>
                <button 
                  onClick={() => handleSectionClick('home')}
                  className={`text-sm font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Início
                </button>
                <button 
                  onClick={() => handleSectionClick('features')}
                  className={`text-sm font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Recursos
                </button>
                <button 
                  onClick={() => handleSectionClick('pricing')}
                  className={`text-sm font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Preços
                </button>
                <button 
                  onClick={() => handleSectionClick('testimonials')}
                  className={`text-sm font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Depoimentos
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/"
                  className={`text-sm font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Início
                </Link>
                <Link 
                  to="/#features"
                  className={`text-sm font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Recursos
                </Link>
                <Link 
                  to="/#pricing"
                  className={`text-sm font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Preços
                </Link>
                <Link 
                  to="/#testimonials"
                  className={`text-sm font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Depoimentos
                </Link>
              </>
            )}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <a 
              href={`${process.env.REACT_APP_APP_URL || 'https://flow.onmai.app.br'}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-sm px-3 py-2 font-medium transition-all duration-300 ${
                isScrolled 
                  ? 'text-gray-800 hover:text-gray-900' 
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Login
            </a>
            <a
              href={`${process.env.REACT_APP_APP_URL || 'https://flow.onmai.app.br'}/register`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackRegister()}
              className={`text-sm px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                isScrolled
                  ? 'bg-white/15 text-gray-800 hover:bg-white/25 backdrop-blur-sm border border-white/15'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              aria-label="Cadastre-se"
            >
              Cadastre-se
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={handleMobileMenuToggle}
              className={`transition-all duration-300 ${
                isScrolled 
                  ? 'text-gray-800 hover:text-gray-900' 
                  : 'text-gray-700 hover:text-gray-900'
              }`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className={`
            md:hidden border-t py-4 transition-all duration-300
            ${isScrolled 
              ? 'border-white/20 bg-white/5 backdrop-blur-md rounded-b-2xl' 
              : 'border-gray-100'
            }
          `}>
            <nav className="flex flex-col space-y-3">
              {isHomePage ? (
                <>
                  <button 
                    onClick={() => handleSectionClick('home')}
                    className={`text-sm font-medium transition-all duration-300 text-left ${
                      isScrolled 
                        ? 'text-gray-800 hover:text-gray-900' 
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Início
                  </button>
                  <button 
                    onClick={() => handleSectionClick('features')}
                    className={`text-sm font-medium transition-all duration-300 text-left ${
                      isScrolled 
                        ? 'text-gray-800 hover:text-gray-900' 
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Recursos
                  </button>
                  <button 
                    onClick={() => handleSectionClick('pricing')}
                    className={`text-sm font-medium transition-all duration-300 text-left ${
                      isScrolled 
                        ? 'text-gray-800 hover:text-gray-900' 
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Preços
                  </button>
                  <button 
                    onClick={() => handleSectionClick('testimonials')}
                    className={`text-sm font-medium transition-all duration-300 text-left ${
                      isScrolled 
                        ? 'text-gray-800 hover:text-gray-900' 
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Depoimentos
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-all duration-300 ${
                      isScrolled 
                        ? 'text-gray-800 hover:text-gray-900' 
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Início
                  </Link>
                  <Link 
                    to="/#features"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-all duration-300 ${
                      isScrolled 
                        ? 'text-gray-800 hover:text-gray-900' 
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Recursos
                  </Link>
                  <Link 
                    to="/#pricing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-all duration-300 ${
                      isScrolled 
                        ? 'text-gray-800 hover:text-gray-900' 
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Preços
                  </Link>
                  <Link 
                    to="/#testimonials"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-all duration-300 ${
                      isScrolled 
                        ? 'text-gray-800 hover:text-gray-900' 
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Depoimentos
                  </Link>
                </>
              )}
              <div className="flex flex-col space-y-2 mt-3">
                <a 
                  href={`${process.env.REACT_APP_APP_URL || 'https://flow.onmai.app.br'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm px-3 py-2 font-medium transition-all duration-300 rounded-lg text-center ${
                    isScrolled 
                      ? 'text-gray-800 hover:text-gray-900 border border-white/20 bg-white/10 backdrop-blur-sm' 
                      : 'text-gray-700 hover:text-gray-900 border border-gray-200'
                  }`}
                >
                  Login
                </a>
                <a
                  href={`${process.env.REACT_APP_APP_URL || 'https://flow.onmai.app.br'}/register`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackRegister()}
                  className={`text-sm px-4 py-2 rounded-lg font-medium transition-all duration-300 text-center ${
                    isScrolled
                      ? 'bg-white/15 text-gray-800 hover:bg-white/25 backdrop-blur-sm border border-white/15'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  aria-label="Cadastre-se"
                >
                  Cadastre-se
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 
